import {
	DURATION_EMPHASIZED_DECELERATE,
	DURATION_EXIT,
	EASE_EMPHASIZED_DECELERATE,
	getEasing,
	SPRING_SMOOTH,
	SPRING_SNAPPY,
} from '@schmancy/utils/animation'
import { reducedMotion$ } from '@schmancy/directives/reduced-motion'
import type { Anchor, OverlayLayout } from './overlay.types'

/**
 * WAAPI keyframes + options per layout + direction.
 *
 * One animation vocabulary — no per-element bespoke keyframes. Every
 * easing runs through `getEasing()` so modern browsers (that support
 * `animation-timing-function: linear(0, 1)`) get the true `linear()`
 * spring expression while older browsers fall through to the cubic-bezier
 * fallback. Honors `prefers-reduced-motion` by collapsing transforms to
 * opacity-only.
 *
 * Anchor-origin bloom: the caller computes
 * `anchorOriginVars(anchor, surfaceRect)` and writes the resulting CSS
 * custom properties onto the surface element BEFORE triggering the
 * entrance animation. Keyframes reference those vars via
 * `transform-origin: var(--schmancy-overlay-origin-x, 50%) var(...)`, so
 * the surface scales OUT of the click point — not from its own centre.
 */

/**
 * Shape matches both DOM `AnimationEffectTiming` and
 * `KeyframeAnimationOptions` so it passes structural checks in both
 * older and newer `lib.dom.d.ts` vintages.
 */
export interface AnimationSpec {
	keyframes: Keyframe[]
	options: {
		duration: number
		easing: string
		fill?: FillMode
	}
}

/**
 * Module-scope reduced-motion state, kept in sync with the house
 * `reducedMotion$` BehaviorSubject. The subscribe has no teardown —
 * module lifetime IS the subscription lifetime.
 */
let reducedMotion = reducedMotion$.value
reducedMotion$.subscribe(v => {
	reducedMotion = v
})

const ORIGIN_X_VAR = '--schmancy-overlay-origin-x'
const ORIGIN_Y_VAR = '--schmancy-overlay-origin-y'
const ORIGIN_CSS = `var(${ORIGIN_X_VAR}, 50%) var(${ORIGIN_Y_VAR}, 50%)`

function clampPercent(value: number): number {
	if (Number.isNaN(value) || !Number.isFinite(value)) return 50
	return Math.max(0, Math.min(100, value))
}

function getAnchorCenter(anchor: Anchor): { x: number; y: number } {
	// Element | VirtualAnchor — anything exposing getBoundingClientRect()
	if (typeof (anchor as { getBoundingClientRect?: unknown }).getBoundingClientRect === 'function') {
		const r = (anchor as { getBoundingClientRect(): DOMRect }).getBoundingClientRect()
		return { x: r.left + r.width / 2, y: r.top + r.height / 2 }
	}
	// DOMRect — has left/top/width/height but no getBoundingClientRect()
	if (
		typeof (anchor as DOMRect).width === 'number' &&
		typeof (anchor as DOMRect).height === 'number' &&
		typeof (anchor as DOMRect).left === 'number' &&
		typeof (anchor as DOMRect).top === 'number'
	) {
		const r = anchor as DOMRect
		return { x: r.left + r.width / 2, y: r.top + r.height / 2 }
	}
	// MouseEvent / PointerEvent
	if (typeof (anchor as MouseEvent).clientX === 'number' && typeof (anchor as MouseEvent).clientY === 'number') {
		const e = anchor as MouseEvent
		return { x: e.clientX, y: e.clientY }
	}
	// TouchEvent
	if (typeof (anchor as TouchEvent).touches !== 'undefined' && (anchor as TouchEvent).touches.length > 0) {
		const t = (anchor as TouchEvent).touches[0]
		return { x: t.clientX, y: t.clientY }
	}
	// Plain { x, y } point
	const pt = anchor as { x: number; y: number }
	return { x: pt.x, y: pt.y }
}

/**
 * Compute CSS-var values for the anchor-relative transform origin.
 * Caller writes the returned record onto the surface element's style
 * before triggering the entrance animation. When no anchor is present,
 * returns sensible per-layout defaults (caller picks which to use):
 *   - centered → 50% 50% (self-centre)
 *   - sheet    → 50% 100% (rises from bottom edge)
 *   - anchored → computed from the anchor's position relative to the
 *                surface rect
 */
export function anchorOriginVars(
	anchor: Anchor | undefined,
	surfaceRect: DOMRect | { left: number; top: number; width: number; height: number },
): Record<string, string> {
	if (!anchor) {
		return {
			[ORIGIN_X_VAR]: '50%',
			[ORIGIN_Y_VAR]: '50%',
		}
	}
	const { x, y } = getAnchorCenter(anchor)
	if (!surfaceRect.width || !surfaceRect.height) {
		return {
			[ORIGIN_X_VAR]: '50%',
			[ORIGIN_Y_VAR]: '50%',
		}
	}
	const originX = clampPercent(((x - surfaceRect.left) / surfaceRect.width) * 100)
	const originY = clampPercent(((y - surfaceRect.top) / surfaceRect.height) * 100)
	return {
		[ORIGIN_X_VAR]: `${originX}%`,
		[ORIGIN_Y_VAR]: `${originY}%`,
	}
}

export function surfaceAnimation(layout: OverlayLayout, direction: 'in' | 'out'): AnimationSpec {
	if (reducedMotion) {
		// Opacity-only; explicit reset to { opacity: 1, transform: none } on `in`
		// so any leftover transform from a prior `out` doesn't persist.
		return direction === 'in'
			? {
					keyframes: [
						{ opacity: 0, transform: 'none' },
						{ opacity: 1, transform: 'none' },
					],
					options: { duration: 1, easing: 'linear', fill: 'forwards' },
				}
			: {
					keyframes: [
						{ opacity: 1, transform: 'none' },
						{ opacity: 0, transform: 'none' },
					],
					options: { duration: 1, easing: 'linear', fill: 'forwards' },
				}
	}

	switch (layout) {
		case 'sheet':
			// The one entrance whose travel is the surface's own full height, so a
			// spring's overshoot is not a flourish here: SPRING_SNAPPY peaks at 1.041,
			// which on a 419px sheet throws it 17px past its resting place and back.
			// Emphasized-decelerate never leaves 0..1, so the sheet arrives and stops.
			// The anchored bloom below keeps that same spring: 4.1% of an 0.85→1 scale
			// is 0.6% of the panel, a pixel or two of its own edge.
			return direction === 'in'
				? {
						keyframes: [
							{ opacity: 0, transform: 'translateY(100%)' },
							{ opacity: 1, transform: 'translateY(0)' },
						],
						options: {
							duration: DURATION_EMPHASIZED_DECELERATE,
							easing: EASE_EMPHASIZED_DECELERATE,
							fill: 'forwards',
						},
					}
				: {
						keyframes: [
							{ opacity: 1, transform: 'translateY(0)' },
							{ opacity: 0, transform: 'translateY(100%)' },
						],
						options: {
							duration: DURATION_EXIT,
							easing: getEasing(SPRING_SMOOTH),
							fill: 'forwards',
						},
					}

		case 'anchored':
			// Anchored surfaces get a scale-from-origin treatment too — the
			// positioning strategy (Popover API / CSS Anchor / Floating UI) owns
			// the final placement; the surface merely blooms from the anchor.
			return direction === 'in'
				? {
						keyframes: [
							{ opacity: 0, transform: 'scale(0.85)', transformOrigin: ORIGIN_CSS },
							{ opacity: 1, transform: 'scale(1)', transformOrigin: ORIGIN_CSS },
						],
						options: {
							duration: SPRING_SNAPPY.duration,
							easing: getEasing(SPRING_SNAPPY),
							fill: 'forwards',
						},
					}
				: {
						keyframes: [
							{ opacity: 1, transform: 'scale(1)', transformOrigin: ORIGIN_CSS },
							{ opacity: 0, transform: 'scale(0.92)', transformOrigin: ORIGIN_CSS },
						],
						options: {
							duration: DURATION_EXIT,
							easing: getEasing(SPRING_SMOOTH),
							fill: 'forwards',
						},
					}

		case 'viewer':
			// The same bloom about the same origin as the anchored panel — the viewer
			// opens out of the thumbnail that was tapped and collapses back onto
			// it — but never through a spring. SPRING_SNAPPY peaks at 1.041, and a
			// 4% overshoot on a BITMAP resamples it a frame past its natural size
			// and back: that reads as a momentary focus pulse, a rendering fault
			// rather than a bounce. Emphasized-decelerate never leaves 0..1.
			//
			// It blooms from 0.12, not from the 0.88 the anchored panel uses, and the
			// difference is the size of the surface rather than a difference of taste.
			// An anchored panel is already content-sized and already placed AT its
			// trigger, so 12% of travel is all it needs to read as arriving from
			// there. The viewer is the whole viewport: scaling the entire screen by
			// 12% about a point is very nearly imperceptible, and what it read as was
			// a plain fade-in of a centred picture — the anchor doing nothing visible
			// at all. From 0.12 the surface starts as a thumbnail-sized thing at the
			// point that was touched and grows to fill the screen, which is the same
			// motion the viewer always claimed to make. 0.1 is what this codebase
			// already collapses a dismissed picture to, so the two ends match.
			return direction === 'in'
				? {
						keyframes: [
							{ opacity: 0, transform: 'scale(0.88)', transformOrigin: ORIGIN_CSS },
							{ opacity: 1, transform: 'scale(1)', transformOrigin: ORIGIN_CSS },
						],
						options: {
							duration: DURATION_EMPHASIZED_DECELERATE,
							easing: EASE_EMPHASIZED_DECELERATE,
							fill: 'forwards',
						},
					}
				: {
						keyframes: [
							{ opacity: 1, transform: 'scale(1)', transformOrigin: ORIGIN_CSS },
							{ opacity: 0, transform: 'scale(0.88)', transformOrigin: ORIGIN_CSS },
						],
						options: {
							duration: DURATION_EXIT,
							easing: EASE_EMPHASIZED_DECELERATE,
							fill: 'forwards',
						},
					}

		default: {
			// The compiler's own proof that every OverlayLayout has a case above.
			// `strict` is off in this package and `noImplicitReturns` is unset, so
			// a missing member falls off the end of this switch and returns
			// `undefined` with NO diagnostic — and the caller does
			// `surface.animate(spec.keyframes, …)`, a TypeError on every open of
			// that layout. Assignability to `never` is not strictness-gated, so
			// this line is the one thing here that fails the build instead.
			const unhandled: never = layout
			return unhandled
		}
	}
}
